import React, { useState } from 'react';

import {
  Button,
  Flex,
  Grid,
  Group,
  Input,
  Radio,
  Stack,
  Text,
  Textarea,
  Title
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconX } from '@tabler/icons-react';

import { useRunSchedulerMutation } from '@/store/slices/api/appointmentApiSlice';
import {
  CreateAppointmentResponse,
  useCreateAppointmentMutation
} from '@/store/slices/api/schedules/schedulesApiSlice';
import { appointmentCreateBody } from '@/utils/appointmentFrequency';
import { getTomorrow } from '@/utils/constants';
import formatDate from '@/utils/formatDate';
import { digitsOnly } from '@/utils/numericFields';
import { runSchedulerForDevices } from '@/utils/runPatientScheduler';
import DatePicker from '@/components/DatePickers/DatePicker';

import styles from '../DeviceScheduleModal.module.scss';
import SchedulesSummary from '../SchedulesSummary';
import { EditScheduleStepProps } from '../types';
import { useAppointmentFrequency } from '../useAppointmentFrequency';

type ScheduleMode = 'single' | 'recurring';

interface FormData {
  startDate: Date | null;
  frequency: string;
  appointmentNotes: string;
}

const initialFormData: FormData = {
  startDate: null,
  frequency: '',
  appointmentNotes: ''
};

const PhysicianRequestedSchedule: React.FC<EditScheduleStepProps> = ({
  onBack,
  onClose,
  onSuccess,
  loading,
  setLoading,
  patientSFId,
  deviceInfo,
  runSchedulerAfterCreate = true
}) => {
  const [showSummary, setShowSummary] = useState(false);
  const [apiResponse, setApiResponse] = useState<CreateAppointmentResponse | null>(null);
  const [scheduleMode, setScheduleMode] = useState<ScheduleMode>('single');
  const [formData, setFormData] = useState<FormData>(initialFormData);

  const [createAppointment] = useCreateAppointmentMutation();
  const [runScheduler] = useRunSchedulerMutation();
  const { frequencyFor } = useAppointmentFrequency(patientSFId);

  const isSingle = scheduleMode === 'single';

  const handleFormChange = <K extends keyof FormData>(field: K, value: FormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!formData.startDate || !patientSFId || !deviceInfo) {
      return;
    }
    if (!isSingle && Number(formData.frequency) < 1) {
      return;
    }

    setLoading(true);

    try {
      const formattedStartDate = formatDate(formData.startDate, 'YYYY-MM-DD');
      const enteredFrequency = isSingle ? 0 : Number(formData.frequency);

      const appointmentData = appointmentCreateBody({
        deviceId: deviceInfo.deviceId,
        patientId: patientSFId,
        scheduledDate: formattedStartDate,
        frequency: frequencyFor('physician request', deviceInfo.type, enteredFrequency),
        type: 'physician request',
        modelNumber: deviceInfo.modelNumber,
        serialNumber: deviceInfo.serialNumber,
        appointmentNotes: formData.appointmentNotes
      });

      const result = await createAppointment(appointmentData).unwrap();

      if (!result.success) {
        console.error('API returned success: false', result);
        notifications.show({
          title: 'Error',
          message: result.message || 'Failed to create Physician Requested appointment',
          color: 'red',
          icon: <IconX size="1.1rem" />,
          autoClose: 5000
        });
        return;
      }

      const failed = runSchedulerAfterCreate
        ? await runSchedulerForDevices(runScheduler, [deviceInfo.deviceId])
        : 0;
      notifications.show({
        title: 'Success',
        message: result.message || 'Physician Requested appointment created successfully',
        color: 'green',
        icon: <IconCheck size="1.1rem" />,
        autoClose: 3000
      });
      if (failed > 0) {
        notifications.show({
          title: 'Schedule not adjusted',
          message:
            'The physician-requested appointment was saved. The other dates could not be shifted.',
          color: 'yellow',
          autoClose: 5000
        });
      }

      setApiResponse(result);
      setShowSummary(true);
    } catch (error) {
      console.error('Error creating Physician Requested appointment:', error);

      if (error && typeof error === 'object' && 'data' in error) {
        const errorData = error.data as CreateAppointmentResponse;
        if (errorData && !errorData.success) {
          notifications.show({
            title: 'Error',
            message: errorData.message || 'Failed to create Physician Requested appointment',
            color: 'red',
            icon: <IconX size="1.1rem" />,
            autoClose: 5000
          });
          return;
        }
      }

      notifications.show({
        title: 'Error',
        message: 'Failed to create Physician Requested appointment',
        color: 'red',
        icon: <IconX size="1.1rem" />,
        autoClose: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const resetAndClose = () => {
    setShowSummary(false);
    setApiResponse(null);
    setScheduleMode('single');
    setFormData(initialFormData);

    if (apiResponse?.success) {
      onSuccess?.();
    }

    onClose();
  };

  if (showSummary && apiResponse?.summary?.nextDates) {
    return (
      <SchedulesSummary
        appointmentType="Physician Request"
        nextDates={apiResponse.summary.nextDates}
        onClose={resetAndClose}
      />
    );
  }

  const saveDisabled =
    !formData.startDate ||
    (!isSingle && Number(formData.frequency) < 1) ||
    loading ||
    !patientSFId ||
    !deviceInfo;

  return (
    <Stack gap="xs">
      <Flex px={20} pt={16} direction="column">
        <Title order={5} fw={600} mb={4} c="plum.9">
          Physician Requested Appointment
        </Title>
        <Text size="xs" c="plum" mb={8}>
          {isSingle
            ? 'Select a scheduled date. Appointment notes are saved on the Salesforce appointment. If any existing remote appointments conflict with account or patient level buffers, the dates will shift to comply with the Physician Requested appointments.'
            : 'Set a start date and how many days between appointments. Appointment notes are saved on the Salesforce appointment. If any existing remote appointments conflict with account or patient level buffers, the dates will shift to comply with the Physician Requested appointments.'}
        </Text>
        <Radio.Group
          value={scheduleMode}
          onChange={(value) => setScheduleMode(value as ScheduleMode)}
          name="physicianRequestScheduleMode"
          label={
            <Text size="xs" c="plum" fw={500} mb={2}>
              Schedule Type
            </Text>
          }>
          <Group gap="lg" mt={2} mb={8}>
            <Radio value="single" label="Single" color="plum.9" disabled={loading} />
            <Radio value="recurring" label="Recurring" color="plum.9" disabled={loading} />
          </Group>
        </Radio.Group>
        <Grid columns={12} mt={8}>
          <Grid.Col span={6}>
            <Text size="xs" c="plum" mb={2} fw={500}>
              Start Date
            </Text>
            <DatePicker
              inputProps={{
                placeholder: 'MM/DD/YYYY',
                required: true,
                disabled: loading
              }}
              datePickerProps={{
                selected: formData.startDate,
                onChange: (date: Date | null) => handleFormChange('startDate', date),
                isClearable: false,
                minDate: getTomorrow(),
                dateFormat: 'MM/dd/yyyy',
                disabled: loading
              }}
            />
          </Grid.Col>
          {!isSingle && (
            <Grid.Col span={6}>
              <Text size="xs" c="plum" mb={2} fw={500}>
                Frequency (Days)
              </Text>
              <Input
                placeholder="Enter frequency in days"
                inputMode="numeric"
                value={formData.frequency}
                onChange={(e) => handleFormChange('frequency', digitsOnly(e.target.value, 4))}
                disabled={loading}
              />
            </Grid.Col>
          )}
          <Grid.Col span={12}>
            <Text size="xs" c="plum" mb={2} fw={500}>
              Appointment Notes
            </Text>
            <Textarea
              placeholder="Add notes for this appointment"
              value={formData.appointmentNotes}
              onChange={(e) => handleFormChange('appointmentNotes', e.currentTarget.value)}
              disabled={loading}
              minRows={3}
              autosize
            />
          </Grid.Col>
        </Grid>
      </Flex>
      <Group className={styles.buttonGroup} justify="flex-end">
        <Button
          variant="outline"
          color="plum.9"
          onClick={onBack}
          className={styles.modalButton}
          disabled={loading}>
          Back
        </Button>
        <Button
          color="plum.9"
          onClick={handleSave}
          className={styles.modalButton}
          disabled={saveDisabled}
          loading={loading}>
          Save
        </Button>
      </Group>
    </Stack>
  );
};

export default PhysicianRequestedSchedule;
